"use client";

import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useRouter } from "next/navigation";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";

import { scrollToElement } from "@/utils/functions";
import { getOngProducts } from "../ongs.service";

import type { GetOngProductsProps } from "@/domain/Ongs/ongs.types";
import type { Product, UseProductListProps } from "@/domain/Ongs/ongs.types";

export function useProductList({ sw, collection }: UseProductListProps) {
  const searchParams = useSearchParams();
  const router = useRouter();

  const productListRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const searchRef = useRef<{ clearInput: () => void }>(null);

  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  const searchQuery = searchParams.get("search") ?? "";
  const pageQuery = searchParams.get("page") ?? "1";

  const getOngProductParams: GetOngProductsProps = {
    queryParams: {
      page: Number(pageQuery),
      query: searchQuery as string
    },
    paths: {
      sw,
      collection
    }
  };

  const { data, isFetching, isError } = useQuery({
    queryKey: ["ong-products", pageQuery, searchQuery, sw, collection],
    queryFn: () => getOngProducts(getOngProductParams),
    enabled: Boolean(sw),
    refetchOnWindowFocus: false,
    throwOnError: true
  });

  const { data: productData, meta } = data || {};

  const products = useMemo(() => productData || [], [productData]);

  const pagination = meta?.pagination;
  const { page, pageCount } = pagination || {};

  const paginationParams = {
    page: page || 1,
    pageCount: pageCount || 12
  };

  const colors = {
    paginationColor: "var(--token-cobasi-colors-turquoise-primary)",
    iconColor: "var(--token-cobasi-colors-turquoise-primary)"
  };

  const isEmpty = data?.data.length === 0 && !isFetching;

  const showAction = Number(pageQuery) !== 1 || searchQuery !== "";

  const handleChangePage = useCallback(
    (selected: number) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", selected.toString());
      params.set("scrollTo", "donation_products");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const onSearchSubmit = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("search", value);
      params.set("page", "1");
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const handleClear = useCallback(() => {
    if (searchRef.current) {
      searchRef.current.clearInput();
    }
    const params = new URLSearchParams(searchParams.toString());
    params.delete("search");
    params.set("page", "1");
    router.push(`?${params.toString()}`, { scroll: false });
  }, [router, searchParams]);

  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  useEffect(() => {
    setIsMobile(document.documentElement.clientWidth < 900);
  }, []);

  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");

    if (scrollTo) {
      hasScrolledRef.current = true;
      setTimeout(() => {
        scrollToElement(scrollTo);
      }, 100);
    }
  }, [searchParams]);

  return {
    page: paginationParams.page,
    pageCount: paginationParams.pageCount,
    colors,
    isEmpty,
    isError,
    isMobile,
    products,
    showAction,
    searchRef,
    productListRef,
    filteredProducts,
    isLoading: isFetching,
    setFilteredProducts,
    handleChangePage,
    onSearchSubmit,
    handleClear
  };
}
